import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  ListChecks,
  Search,
  DollarSign,
  MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Tab = { to: string; label: string; exact?: boolean };
type Section = {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Path prefixes that belong to this section (besides tab `to`s). */
  match: string[];
  tabs: Tab[];
};

export const ADMIN_SECTIONS: Section[] = [
  {
    key: "overview",
    label: "Overview",
    description: "Company-wide platform control center.",
    icon: LayoutDashboard,
    match: ["/dashboard/admin"],
    tabs: [{ to: "/dashboard/admin", label: "Overview", exact: true }],
  },
  {
    key: "listings",
    label: "Listings",
    description: "Provider listings, claims, requests, and data quality.",
    icon: ListChecks,
    match: [
      "/dashboard/admin/listings",
      "/dashboard/admin/claims",
      "/dashboard/admin/requests",
      "/dashboard/admin/avatars",
      "/dashboard/admin/bulk-imports",
      "/dashboard/admin/data-quality",
      "/dashboard/admin/add-listings",
    ],
    tabs: [
      { to: "/dashboard/admin/listings", label: "Overview", exact: true },
      { to: "/dashboard/admin/listings/all", label: "All Listings" },
      { to: "/dashboard/admin/claims", label: "Claims" },
      { to: "/dashboard/admin/requests", label: "Requests" },
      { to: "/dashboard/admin/avatars", label: "Avatars" },
      { to: "/dashboard/admin/data-quality", label: "Data Quality" },
    ],
  },
  {
    key: "seo",
    label: "SEO",
    description: "Indexing, search performance, sitemaps, and content engines.",
    icon: Search,
    match: [
      "/dashboard/admin/seo",
      "/dashboard/admin/seo-search-console",
      "/dashboard/admin/seo-indexnow",
      "/dashboard/admin/seo-programmatic",
      "/dashboard/admin/seo-press",
      "/dashboard/admin/gsc-monitor",
      "/dashboard/admin/sitemap-health",
      "/dashboard/admin/seo-competitor",
      "/dashboard/admin/traffic",
      "/dashboard/admin/blog",
    ],
    tabs: [
      { to: "/dashboard/admin/seo", label: "Overview", exact: true },
      { to: "/dashboard/admin/seo-search-console", label: "GSC" },
      { to: "/dashboard/admin/seo-indexnow", label: "IndexNow" },
      { to: "/dashboard/admin/traffic", label: "Traffic" },
      { to: "/dashboard/admin/sitemap-health", label: "Sitemaps" },
      { to: "/dashboard/admin/seo-programmatic", label: "Programmatic Pages" },
      { to: "/dashboard/admin/seo-competitor", label: "Competitors" },
      { to: "/dashboard/admin/blog", label: "Blog" },
      { to: "/dashboard/admin/seo-press", label: "Press" },
    ],
  },
  {
    key: "revenue",
    label: "Revenue",
    description: "Subscriptions, plans, lifetime deals, and financial performance.",
    icon: DollarSign,
    match: [
      "/dashboard/admin/revenue",
      "/dashboard/admin/revenue-subscriptions",
      "/dashboard/admin/revenue-monthly",
      "/dashboard/admin/revenue-yearly",
      "/dashboard/admin/revenue-featured",
      "/dashboard/admin/revenue-cancellations",
      "/dashboard/admin/lifetime",
    ],
    tabs: [
      { to: "/dashboard/admin/revenue", label: "Overview", exact: true },
      { to: "/dashboard/admin/revenue-subscriptions", label: "Subscriptions" },
      { to: "/dashboard/admin/revenue-monthly", label: "Monthly" },
      { to: "/dashboard/admin/revenue-yearly", label: "Yearly" },
      { to: "/dashboard/admin/revenue-featured", label: "Featured" },
      { to: "/dashboard/admin/revenue-cancellations", label: "Cancellations" },
      { to: "/dashboard/admin/lifetime", label: "Lifetime" },
    ],
  },
  {
    key: "comms",
    label: "Communication",
    description: "Calls, SMS, email, WhatsApp, chatbot, and notifications.",
    icon: MessageSquare,
    match: [
      "/dashboard/admin/comms",
      "/dashboard/admin/comms-email",
      "/dashboard/admin/call",
      "/dashboard/admin/sms",
      "/dashboard/admin/whatsapp",
      "/dashboard/admin/notifications",
      "/dashboard/admin/email-logs",
      "/dashboard/admin/ai",
    ],
    tabs: [
      { to: "/dashboard/admin/comms", label: "Overview", exact: true },
      { to: "/dashboard/admin/call", label: "Calls" },
      { to: "/dashboard/admin/sms", label: "SMS" },
      { to: "/dashboard/admin/comms-email", label: "Email" },
      { to: "/dashboard/admin/whatsapp", label: "WhatsApp" },
      { to: "/dashboard/admin/ai", label: "Chatbot" },
    ],
  },
];

export function getAdminSectionForPath(path: string): Section | null {
  if (!path.startsWith("/dashboard/admin")) return null;
  // Prefer longest-match across all match prefixes and tab `to`s.
  let best: { section: Section; len: number } | null = null;
  for (const section of ADMIN_SECTIONS) {
    const candidates = [...section.match, ...section.tabs.map((t) => t.to)];
    for (const p of candidates) {
      const match = path === p || path.startsWith(p + "/");
      if (match && (!best || p.length > best.len)) {
        best = { section, len: p.length };
      }
    }
  }
  if (best) return best.section;
  // Default to Overview for any unmatched admin path
  return ADMIN_SECTIONS[0];
}

export function AdminSectionNav() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (!path.startsWith("/dashboard/admin")) return null;

  const section = getAdminSectionForPath(path);
  if (!section) return null;

  // For the Overview section with a single tab, hide the tab bar (cleaner).
  const showTabs = section.tabs.length > 1;
  const Icon = section.icon;

  const isTabActive = (tab: Tab) =>
    tab.exact ? path === tab.to : path === tab.to || path.startsWith(tab.to + "/");

  return (
    <div className="sticky top-14 z-20 bg-background border-b border-border">
      <div className="max-w-[1400px] mx-auto px-6 pt-4 pb-2">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h1 className="text-[18px] font-bold tracking-tight leading-tight">
              {section.label}
            </h1>
            <p className="text-[12px] text-muted-foreground leading-tight mt-0.5">
              {section.description}
            </p>
          </div>
        </div>
      </div>
      {showTabs && (
        <div className="max-w-[1400px] mx-auto px-6 overflow-x-auto">
          <nav className="flex items-center gap-1 min-w-max border-b-0">
            {section.tabs.map((tab) => {
              const active = isTabActive(tab);
              return (
                <Link
                  key={tab.to + tab.label}
                  to={tab.to}
                  className={cn(
                    "relative px-3.5 py-2.5 text-[13px] transition-colors -mb-px whitespace-nowrap",
                    active
                      ? "text-primary font-semibold"
                      : "text-muted-foreground hover:text-foreground font-medium",
                  )}
                >
                  {tab.label}
                  {active && (
                    <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </div>
  );
}
